import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import {
    CASE_TYPE_OPTIONS,
    POLE_OPTIONS,
    STATUS_OPTIONS,
    CLOSURE_REASON_OPTIONS,
    RISK_CLASSIFICATION_OPTIONS,
    CREDITOR_ENTITY_OPTIONS,
    LegalCaseType,
    Pole,
    LegalCaseStatus,
    type Beneficiary,
    type RiskClassificationOption,
    type SelectOption,
} from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

// Percentual sugerido para uma classificação CPC 25, a partir das opções carregadas. None → 0.
const suggestedPercentFor = (
    classification: string,
    options: RiskClassificationOption[],
): number => options.find((o) => o.value === classification)?.suggestedLossPercent ?? 0;

interface UseLegalCasePageEditFormArgs {
    uuid: string;
    onClose: () => void;
    onSaved: () => void;
}

// isoDate: o page-view devolve openedAt como ISO datetime (com hora); o DateField/form
// trabalha com yyyy-mm-dd. Recorta a parte da data.
const toDateInput = (iso: string | null): string => (iso ? iso.slice(0, 10) : '');

export default function useLegalCasePageEditForm({ uuid, onClose, onSaved }: UseLegalCasePageEditFormArgs) {
    const [isCreating, setIsCreating] = useState(false); // "salvando" — mesmo nome que o form espera
    const [isLoading, setIsLoading] = useState(true);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);

    const [caseType, setCaseType] = useState<string>(LegalCaseType.JUDICIAL_EXECUTION);
    const [pole, setPole] = useState<string>(Pole.ACTIVE);
    const [status, setStatus] = useState<string>(LegalCaseStatus.OPEN);
    const [creditorEntity, setCreditorEntity] = useState('');
    const [closureReason, setClosureReason] = useState('');
    const [closedAt, setClosedAt] = useState('');
    const [closureNotes, setClosureNotes] = useState('');
    const [openedAt, setOpenedAt] = useState('');
    const [externalCaseNumber, setExternalCaseNumber] = useState('');
    const [court, setCourt] = useState('');
    const [district, setDistrict] = useState('');
    const [courtDivision, setCourtDivision] = useState('');
    const [state, setState] = useState('');
    const [courtLink, setCourtLink] = useState('');
    const [consultationPassword, setConsultationPassword] = useState('');
    const [claimValue, setClaimValue] = useState(0);
    const [responsibleFirmId, setResponsibleFirmId] = useState('');
    const [cpc25Classification, setCpc25ClassificationRaw] = useState('');
    const [estimatedLossPercent, setEstimatedLossPercent] = useState(0);
    const [provisionOverrideAmount, setProvisionOverrideAmount] = useState(0);
    // Encargos (Task 17): começa em zero e é semeado do page-view. Sem sugestão na edição.
    const [interestRateMonthly, setInterestRateMonthly] = useState(0);
    const [penaltyRate, setPenaltyRate] = useState(0);
    const [provisionOverrideReason, setProvisionOverrideReason] = useState('');
    const [description, setDescription] = useState('');

    const [caseTypeOptions, setCaseTypeOptions] = useState<SelectOption[]>(CASE_TYPE_OPTIONS);
    const [poleOptions, setPoleOptions] = useState<SelectOption[]>(POLE_OPTIONS);
    const [statusOptions, setStatusOptions] = useState<SelectOption[]>(STATUS_OPTIONS);
    const [closureReasonOptions, setClosureReasonOptions] =
        useState<SelectOption[]>(CLOSURE_REASON_OPTIONS);
    const [riskClassificationOptions, setRiskClassificationOptions] =
        useState<RiskClassificationOption[]>(RISK_CLASSIFICATION_OPTIONS);
    const [creditorEntityOptions, setCreditorEntityOptions] =
        useState<SelectOption[]>(CREDITOR_ENTITY_OPTIONS);
    // Escritórios cadastrados para o select de escritório responsável (o form não busca de novo).
    const [lawFirms, setLawFirms] = useState<Beneficiary[]>([]);

    // Ao trocar a classificação, sugere o percentual (editável). Limpar zera o percentual.
    const setCpc25Classification = (value: string) => {
        setCpc25ClassificationRaw(value);
        setEstimatedLossPercent(value ? suggestedPercentFor(value, riskClassificationOptions) : 0);
    };

    // Carrega opções dos selects, os escritórios e o caso a ser editado.
    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            try {
                const [opts, firms, detail] = await Promise.all([
                    recoveryApi.getLegalCaseFormOptions().catch(() => null),
                    recoveryApi.getBeneficiaries({ type: 'LAW_FIRM' }).catch(() => null),
                    recoveryApi.getLegalCaseView(uuid),
                ]);
                if (opts?.caseTypes?.length) setCaseTypeOptions(opts.caseTypes);
                if (opts?.poles?.length) setPoleOptions(opts.poles);
                if (opts?.statuses?.length) setStatusOptions(opts.statuses);
                if (opts?.closureReasons?.length) setClosureReasonOptions(opts.closureReasons);
                if (opts?.riskClassifications?.length) setRiskClassificationOptions(opts.riskClassifications);
                if (opts?.creditorEntities?.length) setCreditorEntityOptions(opts.creditorEntities);
                if (firms?.items) {
                    setLawFirms(
                        (firms.items as Beneficiary[]).filter((b) => b.beneficiaryType === 'LAW_FIRM'),
                    );
                }

                setCaseType(detail.caseType);
                setPole(detail.pole);
                setStatus(detail.status);
                setCreditorEntity(detail.creditorEntity || '');
                // Encerramento (Task 20): semeia o desfecho gravado (nulos se o caso está aberto).
                setClosureReason(detail.closureReason || '');
                setClosedAt(toDateInput(detail.closedAt || null));
                setClosureNotes(detail.closureNotes || '');
                setOpenedAt(toDateInput(detail.openedAt));
                setExternalCaseNumber(detail.externalCaseNumber || '');
                setCourt(detail.court || '');
                setDistrict(detail.district || '');
                setCourtDivision(detail.courtDivision || '');
                setState(detail.state || '');
                setCourtLink(detail.courtLink || '');
                setConsultationPassword(detail.consultationPassword || '');
                setClaimValue(detail.claimValue || 0);
                setResponsibleFirmId(detail.responsibleFirmId || '');
                // Risco carregado do bloco `risk` do page-view. Usa o setter cru (não o que
                // sugere) para preservar o percentual guardado, que pode divergir do sugerido.
                setCpc25ClassificationRaw(detail.risk?.cpc25Classification || '');
                setEstimatedLossPercent(detail.risk?.estimatedLossPercent || 0);
                setProvisionOverrideAmount(detail.risk?.provisionOverrideAmount || 0);
                setProvisionOverrideReason(detail.risk?.provisionOverrideReason || '');
                // Encargos (Task 17): carrega as taxas do caso. Ao contrário do form de criação,
                // aqui NÃO se aplica a sugestão — um caso existente sem taxa é estado real (o
                // jurídico pode ter zerado de propósito), e pré-preencher reintroduziria o encargo
                // no próximo save sem ninguém pedir.
                setInterestRateMonthly(detail.interestRateMonthly || 0);
                setPenaltyRate(detail.penaltyRate || 0);
                setDescription(detail.description || '');
            } catch (e: any) {
                toast.error(e?.message || 'Erro ao carregar o caso');
                onClose();
            } finally {
                setIsLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid]);

    const handleCreate = async () => {
        setIsCreating(true);
        setErrorFields([]);
        try {
            await recoveryApi.updateLegalCase(uuid, {
                caseType,
                pole,
                status,
                creditorEntity,
                // Encerramento (Task 20): motivo/data/observação só têm efeito na transição para
                // CLOSED (o backend os exige) ou OPEN (limpa). Envia quando encerrando; o backend
                // ignora fora de uma transição de status.
                closureReason: status === LegalCaseStatus.CLOSED ? (closureReason || undefined) : undefined,
                closedAt: status === LegalCaseStatus.CLOSED ? (closedAt || undefined) : undefined,
                closureNotes: status === LegalCaseStatus.CLOSED ? (closureNotes || undefined) : undefined,
                openedAt: openedAt || undefined,
                externalCaseNumber: externalCaseNumber || undefined,
                court: court || undefined,
                district: district || undefined,
                courtDivision: courtDivision || undefined,
                state: state ? state.toUpperCase() : undefined,
                courtLink: courtLink || undefined,
                consultationPassword: consultationPassword || undefined,
                claimValue: claimValue > 0 ? claimValue : undefined,
                responsibleFirmId: responsibleFirmId || undefined,
                cpc25Classification: cpc25Classification || undefined,
                // envia o percentual se há classificação OU percentual > 0 (parênteses deixam a precedência explícita)
                estimatedLossPercent: (cpc25Classification || estimatedLossPercent > 0)
                    ? estimatedLossPercent : undefined,
                provisionOverrideAmount: provisionOverrideAmount > 0 ? provisionOverrideAmount : undefined,
                provisionOverrideReason: provisionOverrideReason || undefined,
                // Encargos: na **edição**, taxa zerada manda `null` explícito para **limpar** de
                // fato. `undefined` sairia omitido pelo `JSON.stringify` e o full-replace do
                // backend... também limparia — mas o `null` explícito documenta a intenção e
                // sobrevive a uma eventual mudança para semântica PATCH. Mesmo cuidado que a
                // edição da pretensão adotou na AF-3691.
                interestRateMonthly: interestRateMonthly > 0 ? interestRateMonthly : null,
                penaltyRate: penaltyRate > 0 ? penaltyRate : null,
                description: description || undefined,
            });
            onSaved();
            onClose();
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return;
            }
            if (e instanceof ApiError && (e.status === 409 || e.status === 404)) {
                toast.error(e.data?.message || e.message);
                return;
            }
            toast.error(e?.message || 'Erro ao salvar o caso jurídico');
        } finally {
            setIsCreating(false);
        }
    };

    return {
        isLoading,
        isCreating, errorFields,
        caseType, setCaseType,
        pole, setPole,
        status, setStatus,
        creditorEntity, setCreditorEntity,
        closureReason, setClosureReason,
        closedAt, setClosedAt,
        closureNotes, setClosureNotes,
        openedAt, setOpenedAt,
        externalCaseNumber, setExternalCaseNumber,
        court, setCourt,
        district, setDistrict,
        courtDivision, setCourtDivision,
        state, setState,
        courtLink, setCourtLink,
        consultationPassword, setConsultationPassword,
        claimValue, setClaimValue,
        responsibleFirmId, setResponsibleFirmId,
        cpc25Classification, setCpc25Classification,
        estimatedLossPercent, setEstimatedLossPercent,
        interestRateMonthly, setInterestRateMonthly,
        penaltyRate, setPenaltyRate,
        provisionOverrideAmount, setProvisionOverrideAmount,
        provisionOverrideReason, setProvisionOverrideReason,
        description, setDescription,
        caseTypeOptions, poleOptions, statusOptions, closureReasonOptions,
        riskClassificationOptions,
        creditorEntityOptions,
        lawFirms,
        handleCreate,
    };
}
